import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { mailtoHref, outcomeWords, viaWords } from '../../lib/outbox'
import type { OutboundView } from '../../types/outbox'

// The compose panel's pieces, presentation only, so the states are provable without a browser: the play
// chip with the book's why on hover, the editable subject and body, and the four ways out — copy, open in
// Outlook, approve then send from the mailbox (two clicks, never one), discard. Every send is ONE message
// on ONE click (owner rule, 2026-09-17); nothing here has a "send all".

export interface ComposeCardProps {
  view: OutboundView
  subject: string
  body: string
  onSubject: (v: string) => void
  onBody: (v: string) => void
  /** saving is automatic on blur */
  onBlur: () => void
  onCopy: () => void
  onApprove: () => void
  onSend: () => void
  onDiscard: () => void
  /** you sent it from your own tooling and are saying so */
  onSentByHand: (via: 'copy' | 'mailto') => void
  /** the "Not right?" control for the play, rendered by the host (it needs the query client) */
  correct?: ReactNode
  busy?: boolean
  saving?: boolean
  copied?: boolean
  error?: string | null
}

/** "Tactic: Deliver off-clock value", with the mapping's why on hover. Says so when no play fits. */
export function TacticChip({ view, correct }: { view: OutboundView; correct?: ReactNode }) {
  const { row, tacticName } = view
  if (!row.tacticId || !tacticName) {
    return <p className="compose-tactic muted small">No play from the playbook fits this one. {correct}</p>
  }
  return (
    <p className="compose-tactic">
      <span className="tactic-chip" title={row.tacticWhy ?? undefined}>Tactic: {tacticName}</span>
      {correct}
    </p>
  )
}

export function ComposeCard(p: ComposeCardProps) {
  const { view, subject, body, busy = false, saving = false, copied = false, error = null } = p
  const { row } = view
  const editable = row.status === 'drafted' || row.status === 'approved'
  const empty = body.trim().length === 0

  if (row.status === 'sent') {
    return (
      <div className="compose compose-sent">
        <p className="kicker">Sent · {viaWords(row.sentVia)}</p>
        <p><strong>{row.subject || '(no subject)'}</strong> to {row.toName}</p>
        <p className="muted small">How it landed: {outcomeWords(row.outcome)}{row.outcomeSource === 'you' ? ' (your word)' : ''}. <Link to="/outbox">Open the outbox</Link> to change that.</p>
        <TacticChip view={view} correct={p.correct} />
      </div>
    )
  }
  if (row.status === 'discarded') {
    return <div className="compose compose-discarded"><p className="muted small">Discarded. Nothing went out.</p></div>
  }

  return (
    <div className={`compose compose-${row.status}`}>
      <p className="kicker">{row.status === 'approved' ? 'Approved · not sent yet' : 'Draft · in your voice'} · to {row.toName} ({row.toAddress})</p>
      <TacticChip view={view} correct={p.correct} />
      <label className="compose-field">
        <span className="muted small">Subject</span>
        <input value={subject} onChange={(e) => p.onSubject(e.target.value)} onBlur={p.onBlur} disabled={!editable || busy} placeholder="Subject" aria-label="Subject" />
      </label>
      <label className="compose-field">
        <span className="muted small">Message{row.wasEdited ? ' · edited: your words, and Scout learns from them' : ''}</span>
        <textarea rows={10} value={body} onChange={(e) => p.onBody(e.target.value)} onBlur={p.onBlur} disabled={!editable || busy} aria-label="Message" />
      </label>
      {saving && <p className="muted small">Saving…</p>}
      {error && <p className="error small" role="alert">{error}</p>}

      <div className="actions compose-actions">
        <button type="button" className="button small" onClick={p.onCopy} disabled={busy || empty}>{copied ? 'Copied' : 'Copy'}</button>
        <a className="button small" href={mailtoHref({ toAddress: row.toAddress, subject, finalText: body })} onClick={() => p.onBlur()}>Open in Outlook</a>
        {row.status === 'drafted' && (
          <button type="button" className="button small primary" onClick={p.onApprove} disabled={busy || empty} title="Records the play as planned and keeps your wording. Sends nothing.">Approve</button>
        )}
        {row.status === 'approved' && view.canSend && (
          <button type="button" className="button small primary" onClick={p.onSend} disabled={busy || empty} title="Sends this one message from your mailbox, now, once.">Send from my mailbox</button>
        )}
        <button type="button" className="button small" onClick={p.onDiscard} disabled={busy}>Discard</button>
      </div>
      {row.status === 'approved' && !view.canSend && (
        <p className="muted small compose-blocked">{view.sendBlockedWhy} <Link to="/outbox#grant">Set up sending</Link>.</p>
      )}
      {(copied || row.status === 'approved') && (
        <p className="muted small compose-by-hand">
          Sent it yourself? Say so, and it goes on the record:{' '}
          <button type="button" className="link" onClick={() => p.onSentByHand('copy')} disabled={busy}>I pasted it</button>
          {' · '}
          <button type="button" className="link" onClick={() => p.onSentByHand('mailto')} disabled={busy}>I sent it from Outlook</button>
        </p>
      )}
    </div>
  )
}

/** Before a draft exists: the one button, and the plain reason when it cannot be pressed. */
export function ComposeStart({ onStart, busy, modelConfigured, hasAddress, error }: { onStart: () => void; busy: boolean; modelConfigured: boolean; hasAddress: boolean; error?: string | null }) {
  const why = !hasAddress ? 'There is no address on file for this person, so there is nobody to write to.'
    : !modelConfigured ? 'Drafting needs the CLI controller or an API model.' : null
  return (
    <div className="compose compose-start">
      <button type="button" className="button primary" onClick={onStart} disabled={busy || !!why}
        title="A draft in your own voice, written to a play from the playbook, from the reason this person is on your list. You approve every word; nothing is sent until you send it.">
        {busy ? 'Writing…' : 'Write the email'}
      </button>
      {why && <p className="muted small">{why}</p>}
      {error && <p className="error small" role="alert">{error}</p>}
    </div>
  )
}
